import React, { useCallback, useState } from "react";
import type { ChangeEvent } from "react";
import useDrivePicker from "react-google-drive-picker";
import Image from "next/image";
import { SpotToast } from "../SpotToast";
import { useQuery } from "@tanstack/react-query";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import { IntegrationsService } from "@/generated";
import { ExternalLinkedAccountCard } from "../Views/LinkedAccounts";
import { EnvService } from "@/services/Environment/Environment";
import { useSpotUser } from "@/utils/spotUser";
import { Trash2, X } from "lucide-react";
import ImageFallback from "../ImageWithFallback";
import { NoxusIconButton } from "../UI/NoxusIconButton";
import { Button } from "../shadcn/button";
import { cn } from "@/lib/utils";
import { NoxusItem } from "../UI/NoxusItem";
import { usePermissions } from "@/hooks/usePermissions";
import { WORKSPACE_PERMISSIONS } from "@/constants/permissions";

// Always use the custom file browser picker.
export const useNativeGooglePicker = () => false;

interface IGoogleSheetsItem {
  columns: string[];
  sheet_id: string | number;
  title: string;
  max_rows?: number;
}

export type GoogleSheetsMapping = Record<string, IGoogleSheetsItem[]>;

export interface IGooglePickerItem {
  name: string;
  id: string;
  image?: string;
}

interface IGooglePicker {
  selectedItems: IGooglePickerItem[];
  setSelectedItems: (items: IGooglePickerItem[]) => any;
  canSelectDirectories?: boolean;
  canMultiselect?: boolean;
  isLoading?: boolean;
  allowedFiles?: string;
  label: string;
  setIncludeFolders?: boolean;
  isDisabled?: boolean;
}

const parseGoogleLink = (link: string) => {
  const regex = /https:\/\/docs\.google\.com\/([^/]+)\/d\/([^/]+)\/edit/;
  const match = link.match(regex);

  if (match) {
    const fileType = match[1];
    const fileId = match[2];

    return { fileType, fileId };
  } else {
    // TODO change
    return {
      fileType: "spreadsheets",
      fileId: link,
    };
  }
};

interface IUseGooglePicker {
  allowedFiles?: string;
  canSelectDirectories?: boolean;
  canMultiselect?: boolean;
  onSelectItems: (items: IGooglePickerItem[]) => any;
  setIncludeFolders?: boolean;
}

export const useGooglePicker = ({
  allowedFiles,
  canSelectDirectories,
  setIncludeFolders,
  canMultiselect,
  onSelectItems,
}: IUseGooglePicker) => {
  const { spotGroup } = useSpotUser();

  const tokenQuery = useQuery({
    queryKey: ["GOOGLE_TOKEN"],
    queryFn: () =>
      IntegrationsService.getDriveToken({ path: { group_id: spotGroup.id } }),
  });
  const [openPicker] = useDrivePicker();

  const handleOpenPicker = () => {
    if (!gapi) {
      SpotToast.error({
        title: "There was an error loading our Google integration.",
        description:
          "Please refresh the page. If this continues, contact support.",
      });
      return;
    }
    if (tokenQuery.data?.data && tokenQuery.data.data.length > 0) {
      gapi.auth.setToken({
        access_token: tokenQuery.data.data,
        error: "",
        expires_in: "",
        state: "",
      });
    }

    gapi.load("client:auth2", () => {
      gapi.client
        .init({
          apiKey: EnvService.getVar("GOOGLE_API_KEY"),
        })
        .then(() => {
          const tokenInfo = gapi.auth.getToken();

          const pickerConfig: any = {
            clientId: EnvService.getVar("GOOGLE_CLIENT_ID"),
            developerKey: EnvService.getVar("GOOGLE_API_KEY"),
            viewMimeTypes: allowedFiles,
            setIncludeFolders: setIncludeFolders,
            setSelectFolderEnabled: canSelectDirectories,
            token: tokenInfo?.access_token ? tokenInfo.access_token : null,
            multiselect: canMultiselect,
            scopes: ["https://www.googleapis.com/auth/drive.file"],
            appId: EnvService.getVar("GOOGLE_APP_ID"),
            callbackFunction: (data: any) => {
              if (data.action === "picked") {
                onSelectItems(
                  data.docs.map((doc: any) => {
                    return {
                      image: doc.iconUrl,
                      name: doc.name,
                      id: doc.id,
                      mime_type: doc.mimeType,
                    };
                  })
                );
              }
            },
          };
          openPicker(pickerConfig);
        });
    });
  };

  return { handleOpenPicker };
};

const GooglePicker = ({
  label,
  canMultiselect = true,
  canSelectDirectories = true,
  selectedItems,
  setSelectedItems,
  isLoading,
  allowedFiles,

  setIncludeFolders = false,
  isDisabled = false,
}: IGooglePicker) => {
  const { spotGroup } = useSpotUser();
  const { hasPermission } = usePermissions();
  const [isLoadingFile, setIsLoadingFile] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>("");

  const canEditIntegrations = hasPermission(
    WORKSPACE_PERMISSIONS.EDIT,
    "group"
  );

  // Links can't work properly due to drive.file Oauth scope

  const { data: integrationsData } = useRemoteData(DATA_KEYS.INTEGRATIONS, {
    path: { group_id: spotGroup.id },
  });

  const isGoogleDriveEnabled = integrationsData?.gdrive;

  const isFolder = allowedFiles === "application/vnd.google-apps.folder";

  const { handleOpenPicker } = useGooglePicker({
    allowedFiles,
    canSelectDirectories,
    canMultiselect,
    onSelectItems: setSelectedItems,
    setIncludeFolders,
  });

  const getFileMetadata = useCallback(
    (fileId: string, fileType: string) => {
      setIsLoadingFile(true);

      if (fileType === "spreadsheets") {
        IntegrationsService.getSpreadsheetInformation({
          path: {
            group_id: spotGroup.id,
            spreadsheet_id: fileId,
          },
          query: {
            max_column: undefined,
          },
        })
          .then((response) => {
            setSelectedItems([
              {
                image:
                  "https://storage.googleapis.com/image-storage-spot-manual/logos/google-sheets.svg",
                name: response.data.title,
                id: fileId,
              },
            ]);
          })
          .catch(() => {
            SpotToast.error("Could not retrieve spreadsheet information.");
          })
          .finally(() => {
            setIsLoadingFile(false);
          });
      } else if (fileType === "document") {
        IntegrationsService.getDocsInformation({
          path: {
            group_id: spotGroup.id,
            doc_id: fileId,
          },
        })
          .then((response) => {
            setSelectedItems([
              {
                image:
                  "https://storage.googleapis.com/image-storage-spot-manual/logos/google-docs.png",
                name: response.data.title,
                id: fileId,
              },
            ]);
          })
          .catch(() => {
            SpotToast.error("Could not retrieve docs information.");
          })
          .finally(() => {
            setIsLoadingFile(false);
          });
      } else {
        SpotToast.error("File type not supported.");
        setIsLoadingFile(false);
      }
    },
    [setSelectedItems, spotGroup]
  );

  const handleGoogleLink = useCallback(
    (providedInputValue: string) => {
      if (isDisabled) return;
      const parsedLinkData = parseGoogleLink(providedInputValue);
      if (parsedLinkData == null) {
        SpotToast.error(
          "There was an error processing the link provided. Please make sure the link is correct."
        );
      } else {
        getFileMetadata(parsedLinkData.fileId, parsedLinkData.fileType);
      }
    },
    [getFileMetadata, isDisabled]
  );

  const handlePaste = useCallback(
    (e: any) => {
      setInputValue(e.clipboardData.getData("text"));
      handleGoogleLink(e.clipboardData.getData("text"));
    },
    [handleGoogleLink]
  );

  const handleDelete = (e: any, id: string) => {
    e.stopPropagation();
    setSelectedItems(selectedItems.filter((si) => si.id !== id));
  };

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (isDisabled) return;
      setInputValue(e.target.value);
    },
    [isDisabled]
  );

  const handleFileCilck = useCallback((file: IGooglePickerItem) => {
    const win = window.open(
      `https://drive.google.com/open?id=${file.id}`,
      "_blank"
    );
    win?.focus();
  }, []);

  // Prepare for multiselect
  if (canMultiselect) {
    return (
      <div>
        <GoogleButton
          text="Choose files"
          onClick={handleOpenPicker}
          isDisabled={isDisabled}
        />
        {selectedItems.length > 0 && (
          <div className="flex flex-col mt-6 gap-2 p-0 w-full max-h-60 overflow-y-auto">
            {selectedItems.map((item) => (
              <DeletableElement
                key={`item-el-${item.id}`}
                elName={item.name}
                elId={item.id}
                elImage={item?.image}
                handleClick={() => handleFileCilck(item)}
                handleDelete={handleDelete}
                isDisabled={isDisabled}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <p className="text-xs text-muted-foreground mb-1">
        {isFolder ? "Folder" : selectedItems.length > 0 ? "File" : "Link"}
      </p>
      {selectedItems.length === 0 ? (
        isGoogleDriveEnabled ? (
          <GoogleButton
            onClick={handleOpenPicker}
            text={isFolder ? "Choose folder" : undefined}
          />
        ) : (
          <ExternalLinkedAccountCard
            slug={"gdrive"}
            isDisabled={!canEditIntegrations}
          />
        )
      ) : (
        <div className="flex items-center justify-between gap-1">
          <div className="flex-grow bg-background border border-border rounded-lg focus-within:border-muted-foreground items-center py-2 px-2 flex-wrap h-10 flex">
            {selectedItems.map((item, index) => (
              <div
                key={item.id}
                className={cn(
                  "flex items-center flex-wrap border-0 rounded-lg px-2",
                  isLoading || isLoadingFile
                    ? "opacity-60 cursor-not-allowed"
                    : "cursor-pointer"
                )}
                onClick={() => handleFileCilck(item)}
              >
                {item.image && (
                  <div className="relative w-4 h-4 mr-2">
                    <ImageFallback fill alt="" src={item.image} />
                  </div>
                )}
                <p className="text-sm max-w-40 truncate">{item.name}</p>

                <NoxusIconButton
                  icon={X}
                  aria-label="remove file"
                  variant="tertiary"
                  isLoading={isLoadingFile || isLoading}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleDelete(e, item.id);
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const GoogleButton = ({
  onClick,
  text,
  isDisabled = false,
}: {
  onClick: () => any;
  text?: string;
  isDisabled?: boolean;
}) => {
  return (
    <Button variant="outline" onClick={onClick} isDisabled={isDisabled}>
      <Image
        alt="Google logo"
        src="/images/ai-engine/external-integrations/google.png"
        width={20}
        height={20}
      />
      {text || "Choose file"}
    </Button>
  );
};

const DeletableElement = ({
  elName,
  elId,
  elImage,
  handleClick,
  handleDelete,
  isDisabled = false,
}: {
  elName: string;
  elId: string;
  elImage?: string;
  handleClick: any;
  handleDelete: (e: any, id: string) => void;
  isDisabled?: boolean;
}) => {
  return (
    <div
      onClick={handleClick}
      className={cn("w-full", !isDisabled && "cursor-pointer")}
    >
      <NoxusItem
        icon={elImage || undefined}
        iconAlt={elName}
        title={elName}
        className="bg-background2"
        iconClassName="bg-white"
        actions={
          <Button
            aria-label="Remove"
            size="iconsm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(e, elId);
            }}
            disabled={isDisabled}
          >
            <Trash2 strokeWidth={1.33} />
          </Button>
        }
      />
    </div>
  );
};

export default GooglePicker;
